<template>
  <div class=" px-5 mx-7 pt-5">
    <Card :title="$t('pipelines.list')" containerClass="mb-5">
      <template #cardToolbar>
        <div class="d-flex justify-content-end align-items-center">
          <div>
            <Button type="new" v-if="$can(abilities.CREATE_OPPORTUNITIES)" btn-class="btn-sm me-2 text-end" btn-text="pipelines.create" @click="openPipelineModal" ></Button>
          </div>
        </div>
      </template>
      <template #cardBody>
        <div class="row">
          <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="pipelines_datatable">
            <thead>
              <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                <th class="min-w-100px">{{ $t('pipelines.label.opportunity_closing_date') }}</th>
                <th class="min-w-150px">{{ $t('pipelines.label.label') }}</th>
                <th class="min-w-150px">{{ $t('crm.pipeline.label.amount') }} ({{ defaultCurrency?.iso }})</th>
                <th class="min-w-150px">{{ $t('pipelines.label.prospect') }}</th>
                <th class="min-w-150px">{{ $t('pipelines.label.state') }}</th>
                <th class="min-w-50px">{{ $t('label.action') }}</th>
              </tr>
            </thead>
          </table>
        </div>
      </template>
    </Card>
  </div>
  <CreateOpportunity id="create_pipeline_modal" :form="createOpportunityForm" @load="fetchDatas" />
</template>

<script setup>
import {computed, defineProps, nextTick, onMounted, ref, shallowRef, watch} from "vue";
import i18n from "@/plugins/i18n";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import Ls from "@/services/ls";
import {formattedLocalAmount} from "@/mixins/Helpers";
import {useHasAbility} from "@/composables/hasAbility";
import {useLocales} from "@/composables/locales";
import { BASE_URL } from "@/services/api";
import Button from "@/components/Button";
import Card from "@/Modules/CRM/components/Card";
import CreateOpportunity from "@/Modules/CRM/components/Modals/Pipeline/CreateOpportunity.vue";
import {Theme} from "@/composables/theming";
import abilities from "@/composables/abilities";

const store = useStore()
const router = useRouter()
const route = useRoute()
const { can } = useHasAbility()
const {locale, datatableLocaleUrl} = useLocales()

const props = defineProps({
  filter: {
    type: Object,
    default: () => ({
      startDate: '',
      endDate: '',
      status: '',
      contact: '',
      search: '',
      priority: ''
    })
  },
  openPipelineModal:Function
})
const table = ref(null)
const createOpportunityForm = ref({
  id: null,
  name: "",
  expected_income: "0",
  creation_date: null,
  closing_date: null,
  opportunity_source_id: "",
  contact_id: "",
  description: "",
  priority: 0,
  opportunity_state_id: '',
  products: [],
  collaborators: [],
  tags: [],
  files: []
})
const query = ref(props.filter)

const defaultCurrency = computed(() => store.getters['company/getCompanyCurrency'])


watch(() => locale.value, (value) => {
  nextTick(() => {
    if(query.value.startDate && query.value.endDate){
      fetchDatas()
    }
  } )
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  if(newValue){
    fetchDatas()
  }
})

watch(() => query.value.seller, (newValue) => {
  if(newValue){
    fetchDatas()
  }
})

watch(() => query.value.priority, (newValue) => {
  if(newValue){
    fetchDatas()
  }
})

watch(() => query.value.contact, (newValue) => {
  if(newValue){
    fetchDatas()
  }
})

watch(() => [query.value.startDate, query.value.endDate], (newValue) => {
  if(newValue){
    fetchDatas()
  }
})

function openPipelineModal(){
  $('#create_pipeline_modal').modal('toggle')
}

function fetchDatas() {
  const AUTH_TOKEN = Ls.get('auth.token')
  const pageLimit = Ls.get('pageLength')

  table.value = $("#pipelines_datatable").DataTable({
    destroy: true,
    responsive: !0,
    searchDelay: 500,
    processing: !0,
    serverSide: !0,
    pageLength: pageLimit ||  5,
    lengthMenu: [5, 10, 25, 50, 75, 100 ],
    order: [[ 1, 'asc']],
    ajax: {
      url: BASE_URL + `/get/opportunities/datatables?startDate=${props.filter.startDate}&endDate=${props.filter.endDate}&contact_id=${props.filter.contact}&status=${props.filter.status}&seller_id=${props.filter.seller}&priority=${props.filter.priority}`,
      type: "GET",
      beforeSend: function (request) {
        request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
        request.setRequestHeader("Company", Ls.get('current.company'));
      }
    },
    columns: [
      { data: "closing_date" }, { data: "name" }, { data: "expected_income" }, { data: "contact" }, { data: "opportunityState" }, { data: "Actions", responsivePriority: -1 }
    ],
    columnDefs: [

      {
        targets: -6,
        orderable: false,
        className: "dt-center clickable" ,
        render: function (t, a, e, l) {
          const date = t ? t : '---'
          return '<span data-id="'+ e.id +'">' + date + "</span>";
        },
      },

      {
        width:'25%',
        targets: -5,
        order: "desc",
        className: "dt-center clickable" ,
        render: function (t, a, e, l) {
            return `<span data-id="${e.id}">${t}${e.status == "WON" ? `<i class="emojione-monotone--money-with-wings fs-2 text-primary me-2 mx-4" data-id="${e.id}"></i>` : e.status == "LOST" ?`<i class="fa fa-heart-broken text-danger me-2 fs-2 mx-4" data-id="${e.id}"></i>` :''}</span>`; 
        },
      },
      {
        targets: -4,
        order: "desc",
        className: "dt-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        width:'25%',
        targets: -3,
        orderable: false,
        className: "dt-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + t + "</span>";
        },
      },

      {
        targets: -2,
        orderable: false,
        className: "dt-center clickable" ,
        render: function (t, a, e, l) {
          return '<span data-id="'+ e.id +'">' + formattedLocalAmount(t)  + "</span>";
        },
      },


      {
        targets: -1,
        "className": "dt-center text-center",
        title: "Actions",
        orderable: !1,
        render: (t, a, e, l) => {
          let render=""
          if(can(abilities.VIEW_OPPORTUNITIES) || can(abilities.VIEW_ALL_OPPORTUNITIES)){
            render += `<a href="javascript:;" data-row="${l.row}" data-id="${e.id}" data-name="${e.name}" class="btn btn-sm btn-clean btn-icon show_btn" title="${i18n.global.t('button.edit')}"><i data-row="${l.row}" data-id="${e.id}" data-name="${e.name}" class="fas fa-edit fs-2" style="color: ${Theme.gray}" ></i></a>`;
          }
          return render
        },
      },

    ],
    language: {
      url: datatableLocaleUrl.value,
    }
  });

  $(document).on("click", "#pipelines_datatable .show_btn", (e) => {
    router.push({ name: 'pipelines-view', params: { id: e.target.getAttribute('data-id') } })
  });

  $(document).on("click", "#pipelines_datatable .edit_btn", (e) => {
    router.push({ name: 'pipelines-edit', params: { id: e.target.getAttribute('data-id') } })
  });

}

onMounted(() => {
  store.dispatch('company/generalInformation')
  if(query.value.startDate && query.value.endDate){
    fetchDatas()
  }
})

</script>

<style scoped>

</style>

